import {
  getFormatDetailRows,
  getFormatTypeLabel,
} from '@src/utility/distributionChannelComparison';
import { Fragment, useId } from 'react';
import { Tooltip } from 'react-tooltip';
import styled from 'styled-components';

import ChannelIcon from './ChannelIcon';
import {
  CHANNEL_TOOLTIP_STYLE,
  renderChannelFormatTooltip,
} from './channelTooltipContent';

import type { DistributionChannel } from '@src/types.ts/distribution-channel';

interface Props {
  channel: DistributionChannel;
  onRemove?: () => void;
  removeDisabled?: boolean;
  variant?: 'summary' | 'details';
}

const DistributionChannelRow = ({
  channel,
  onRemove,
  removeDisabled,
  variant = 'details',
}: Props) => {
  const tooltipBase = useId();
  return (
    <Row>
      <Body>
        <TitleRow>
          <ChannelIcon
            iconUrl={channel.iconUrl}
            alt={`${channel.title} icon`}
            size={ICON_SIZE}
          />
          <ChannelName>{channel.title}</ChannelName>
          {variant === 'summary' && channel.formats.length > 0 && (
            <SummaryFormats>
              {channel.formats.map((format, idx) => {
                const tipId = `${tooltipBase}-fmt-${format.id}`;
                return (
                  <Fragment key={format.id}>
                    {idx > 0 && <SummarySeparator>, </SummarySeparator>}
                    <FormatTypeBadge data-tooltip-id={tipId}>
                      {getFormatTypeLabel(format.formatType)}
                    </FormatTypeBadge>
                    <Tooltip
                      id={tipId}
                      noArrow
                      render={() =>
                        renderChannelFormatTooltip({ channel, format })
                      }
                      style={CHANNEL_TOOLTIP_STYLE}
                    />
                  </Fragment>
                );
              })}
            </SummaryFormats>
          )}
        </TitleRow>
        {channel.description && (
          <Description>{channel.description}</Description>
        )}
        {variant === 'details' && channel.formats.length > 0 && (
          <DetailGrid>
            {channel.formats.map((format, idx) => (
              <Fragment key={format.id}>
                <FormatName $isFirst={idx === 0}>
                  {getFormatTypeLabel(format.formatType)}
                </FormatName>
                {getFormatDetailRows(format).map(row => (
                  <Fragment key={`${format.id}:${row.aspect}`}>
                    <DetailLabel>{row.label}</DetailLabel>
                    <DetailValue>{row.value}</DetailValue>
                  </Fragment>
                ))}
              </Fragment>
            ))}
          </DetailGrid>
        )}
      </Body>
      {onRemove && (
        <RemoveButton
          type="button"
          onClick={onRemove}
          disabled={removeDisabled}
          aria-label={`Remove ${channel.title}`}
        >
          Remove
        </RemoveButton>
      )}
    </Row>
  );
};

export default DistributionChannelRow;

const Row = styled.div`
  display: flex;
  gap: 16px;
  align-items: flex-start;
  padding: 16px 0;
`;

const Body = styled.div`
  flex: 1 1 auto;
  display: flex;
  flex-direction: column;
  gap: 6px;
`;

// Layout constants — one source of truth so the description's left-pad lines
// up with the channel-name column even if the icon column changes.
const ICON_SIZE = 32;
const ICON_GAP = 12;
// Channel-name column is fixed-width so the supported-formats list starts at
// the same x across every network.
const CHANNEL_NAME_COL = 180;

const TitleRow = styled.div`
  display: grid;
  grid-template-columns: ${ICON_SIZE}px ${CHANNEL_NAME_COL}px minmax(0, 1fr);
  align-items: center;
  gap: ${ICON_GAP}px;
`;

const ChannelName = styled.h3`
  font-size: var(--text-base);
  font-weight: var(--font-weight-bold);
  color: var(--foreground);
  margin: 0;
`;

const Description = styled.p`
  font-size: var(--text-sm);
  color: var(--text-secondary);
  margin: 0;
  /* Indent description under the channel name so it lines up with the title,
     not the icon. */
  padding-left: ${ICON_SIZE + ICON_GAP}px;
`;

const SummaryFormats = styled.div`
  --rt-opacity: 1;
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  font-size: var(--text-xs);
  color: var(--text-secondary);
`;

const SummarySeparator = styled.span`
  color: var(--text-muted);
  padding: 0 2px;
`;

const FormatTypeBadge = styled.span`
  font-weight: var(--font-weight-bold);
  text-transform: uppercase;
  letter-spacing: 0.04em;
  color: var(--foreground);
`;

const FormatName = styled.div<{ $isFirst: boolean }>`
  grid-column: 1 / -1;
  font-weight: var(--font-weight-bold);
  text-transform: uppercase;
  letter-spacing: 0.04em;
  color: var(--foreground);
  margin-top: ${props => (props.$isFirst ? '0' : '14px')};
  margin-bottom: 2px;
`;

const DetailGrid = styled.div`
  display: grid;
  /* Label sized to longest label across all formats so values stay close to
     labels but still align in one column for the whole channel. */
  grid-template-columns: max-content minmax(0, 1fr);
  column-gap: 24px;
  row-gap: 4px;
  /* Align with channel name + description so details sit under the title
     column, not under the icon. */
  padding-left: ${ICON_SIZE + ICON_GAP}px;
  font-size: var(--text-sm);
`;

const DetailLabel = styled.div`
  color: var(--text-secondary);
`;

const DetailValue = styled.div`
  color: var(--foreground);
  font-variant-numeric: tabular-nums;
`;

const RemoveButton = styled.button`
  align-self: flex-start;
  background: transparent;
  border: 1px solid var(--border);
  border-radius: 6px;
  padding: 4px 10px;
  font-size: var(--text-sm);
  color: var(--text-secondary);
  cursor: pointer;
  transition:
    color var(--transition-fast) ease,
    border-color var(--transition-fast) ease,
    background var(--transition-fast) ease;

  &:hover:not(:disabled) {
    background: var(--surface-elevated);
    color: var(--foreground);
    border-color: var(--border-strong, var(--border));
  }

  &:disabled {
    opacity: 0.5;
    cursor: progress;
  }
`;
